import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import type {
  EnvironmentKey,
  ConfigEntity,
  ConfigEntityConfiguration,
  DomainEntity,
  EntityDetail,
  EntityRootData,
  OperationType,
  GetEndpointResponse,
  ConfigApiError,
  GenerateDomainEntityId,
  ConfigEntityRootData,
} from '@web-config-app/core';
import {
  isConfigEntity,
  isConfigEntityConfiguration,
  operationToConfigEntityAuthAction,
  getEntityOperationConfig,
} from '@web-config-app/core';
import { buildConfigEntityAuthHeaders } from '@web-config-app/api';
import { getEntityEndpoint } from '../../utilities/get-entity-endpoint/get-entity-endpoint.util';
import {
  OnMutateWithOperation,
  OnSuccessWithEnvironment,
} from '../../types/operation.types';
import { useEnvironmentFetchFactory } from '../use-environment-fetch/use-environment-fetch';
import { useUserInfo } from '../use-user-info/use-user-info';
import { prepareEntityBodyData } from '../../utilities/prepare-entity-body-data/prepare-entity-body-data';

export type EntityTransformer = (data: EntityRootData) => EntityRootData;

export type UseEntityCreateResult = ReturnType<typeof useEntityCreate>;

/**
 * The mutation variable type for useEntityCreate.
 * - `void` — uses the internally-built body from the hook's `data` prop
 * - `BodyInit` — uses the provided body directly (e.g. JSON string or FormData)
 * - `{ body: BodyInit; domainEntityType?: string }` — uses the provided body and
 *   optionally overrides the X-League-Config-Entity-Type header with the given
 *   domainEntityType (e.g. 'config-activity') instead of the entity config type
 */
export type EntityCreateMutationVariable =
  | {
      body: BodyInit;
      domainEntityType?: string;
      /**
       * Create in this environment instead of the one the hook was configured with.
       *
       * Lets a caller that chooses its target per operation -- promoting an entity to one of
       * several preview environments, say -- issue the create immediately, rather than
       * re-pointing the hook and waiting a render for it to rebind.
       */
      environment?: EnvironmentKey;
    }
  | BodyInit
  | void;

export type UseEntityCreateOptions<SuccessData = GetEndpointResponse> = Pick<
  UseMutationOptions<GetEndpointResponse, Error, EntityCreateMutationVariable>,
  'onError' | 'onSettled'
> & {
  onMutate?: OnMutateWithOperation;
  fetchFn?: (path: string, options: any) => Promise<Response>;
  onSuccess?: OnSuccessWithEnvironment<SuccessData>;
  transformers?: EntityTransformer[];
};

const resolveMutationEnvironment = (
  mutateVariable: EntityCreateMutationVariable,
  configuredEnvironment: EnvironmentKey,
): EnvironmentKey =>
  (typeof mutateVariable === 'object' &&
  mutateVariable !== null &&
  'environment' in mutateVariable
    ? mutateVariable.environment
    : undefined) ?? configuredEnvironment;

export interface UseEntityCreateProps {
  // Only types with required `endpoints` are accepted — callers must hand in a fully-resolved
  // entity (post `buildEntityConfigs`), not a bare `DomainEntityConfiguration` whose endpoints
  // could still be undefined.
  entity: ConfigEntity | ConfigEntityConfiguration | DomainEntity;
  data?: EntityRootData | ConfigEntityRootData;
  included?: Array<Partial<EntityDetail>>;
  headers?: HeadersInit;
  environment: EnvironmentKey;
  options?: UseEntityCreateOptions;
  operation: Extract<OperationType, 'create' | 'copyToEnvironment' | 'clone'>;
}

type EntityCreateOptions = {
  apiUrl?: string;
  generateDomainEntityId?: GenerateDomainEntityId;
};

const getEntityCreateOptions = (
  entity: UseEntityCreateProps['entity'],
): EntityCreateOptions => {
  if (isConfigEntity(entity) || isConfigEntityConfiguration(entity)) {
    return isConfigEntity(entity) ? entity.domainEntity : {};
  }

  return entity;
};
/**
 * useEntityCreate - The following hook is responsible for calling the BE API associated to the Create (POST) service of an entity.
 * @param props
 * @param props.entity - the whole {@link Entity} that exposes the needed values including the id, endpoints, etc.
 * @param props.data - the root data of the form used when saving the entity data
 * @param props.headers - optional headers to pass into the fetch function
 * @param props.options - optional config object
 * @param props.options.fetchFn - You can pass in your own custom fetch function to be used, instead of the default currentEnvironmentFetch
 * @param props.options.transformers - Array of transformation functions to apply to the data before sending to the backend
 * @returns the result of useMutation, which is then used by our EntityDetailsProvider, who is responsible for relaying the result where needed.
 */
export const useEntityCreate = ({
  entity,
  data,
  included,
  headers,
  options,
  environment,
  operation,
}: UseEntityCreateProps) => {
  // The config-entities backend has three distinct POST endpoints — `create`, `clone`, and
  // `copyToEnvironment` — so the caller picks one via `operation`. Non-CACT-BE backends
  // (`isUsingConfigAppBackend: false`) expose a single POST endpoint, so we ignore `operation`
  // for those.
  // TODO(CACT-1920): drop this once everything is CACT-BE.
  const configOperation =
    entity.type === 'config-entity' ? operation : 'create';
  const operationEndpoint = entity.endpoints[configOperation];
  const { apiUrl, generateDomainEntityId } = getEntityCreateOptions(entity);

  let mutationFn;
  // A factory rather than a bound fetch, so a caller can name the target environment on the
  // mutation itself (see EntityCreateMutationVariable.environment).
  const createEnvironmentFetch = useEnvironmentFetchFactory();
  const { userInfo } = useUserInfo();

  // TODO:: Remove check for entity.type === 'config-entity' once we are fully migrated to the new backend
  // https://everlong.atlassian.net/browse/CACT-1920
  const shouldInjectDomainEntityId =
    Boolean(generateDomainEntityId?.enabled) && entity.type === 'config-entity';

  if (operationEndpoint) {
    const domainEntityConfig = isConfigEntity(entity)
      ? entity.domainEntity
      : entity;

    const domainEntityStatus = isConfigEntityConfiguration(domainEntityConfig)
      ? undefined
      : getEntityOperationConfig(domainEntityConfig, operation)
          ?.requiredEntityMetadataFieldValues?.status ??
        (domainEntityConfig.isUsingConfigAppBackend === false
          ? domainEntityConfig.endpoints[operation]
              ?.requiredEntityMetadataFieldValues?.status
          : undefined);
    const shouldUseEntityStatus =
      Boolean(domainEntityStatus) &&
      (isConfigEntityConfiguration(domainEntityConfig) ||
        !domainEntityConfig.draftingEnabled);

    const overrideOperationEndpoints = {
      ...operationEndpoint,
      requiredEntityMetadataFieldValues: {
        ...operationEndpoint.requiredEntityMetadataFieldValues,
        ...(shouldUseEntityStatus ? { status: domainEntityStatus } : {}),
      },
    };

    const defaultDomainEntityType = isConfigEntity(entity)
      ? entity.domainEntity.type
      : entity.type;
    const action = operationToConfigEntityAuthAction[operation];

    mutationFn = (mutateVariable: EntityCreateMutationVariable) => {
      const bodyData = prepareEntityBodyData({
        data,
        entity,
        operationEndpoint: overrideOperationEndpoints,
        userInfo,
        transformers: options?.transformers,
        shouldInjectDomainEntityId,
        generateDomainEntityId,
      });

      const defaultBody = JSON.stringify({
        data: bodyData,
        ...(included && { included }),
      });

      // Extract body and optional domainEntityType override from the mutation variable
      const isObjectVariable =
        typeof mutateVariable === 'object' &&
        mutateVariable !== null &&
        'body' in mutateVariable;
      const resolvedBody = isObjectVariable
        ? mutateVariable.body
        : mutateVariable || defaultBody;
      const resolvedDomainEntityType = isObjectVariable
        ? mutateVariable.domainEntityType ?? defaultDomainEntityType
        : defaultDomainEntityType;

      // Resolved per call so the caller can redirect a single create at another environment.
      const { endpointFetch } = getEntityEndpoint<GetEndpointResponse>(
        operationEndpoint,
        options?.fetchFn ??
          createEnvironmentFetch(
            resolveMutationEnvironment(mutateVariable, environment),
            apiUrl,
          ),
      );

      return endpointFetch({
        body: resolvedBody,
        headers: {
          ...headers,
          ...buildConfigEntityAuthHeaders(resolvedDomainEntityType, action),
        },
      });
    };
  }

  const mutation = useMutation<
    GetEndpointResponse,
    ConfigApiError,
    EntityCreateMutationVariable
  >({
    mutationFn,
    // Report the environment the create actually targeted, which is the per-call override
    // when there is one -- callers use this to invalidate the right environment's cache.
    onSuccess: (responseData, mutateVariable) =>
      options?.onSuccess?.(
        responseData,
        resolveMutationEnvironment(mutateVariable, environment),
      ),
    onError: options?.onError,
    onMutate: (mutateVariable) =>
      options?.onMutate?.(
        operation,
        resolveMutationEnvironment(mutateVariable, environment),
      ),
    onSettled: options?.onSettled,
  });

  return {
    ...mutation,
    actionLabel: operationEndpoint?.actionLabel,
    actionType: operationEndpoint?.actionType,
    enableAction: Boolean(operationEndpoint),
  };
};
